import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Link2, Loader2, Search } from 'lucide-react';

// Reusable multi-select picker for linking existing property records to the current project.
// Records passed in are already filtered to the project's property and exclude ones already
// linked to this project. Linking is additive (a record can belong to multiple projects).
export function LinkExistingDialog({
  open, onOpenChange, title, description, records,
  renderPrimary, renderSecondary, renderBadge, linking, onConfirm, emptyText, testid = 'link-existing',
}) {
  const [selected, setSelected] = useState({});
  const [q, setQ] = useState('');

  useEffect(() => { if (open) { setSelected({}); setQ(''); } }, [open]);

  const list = Array.isArray(records) ? records : [];
  const filtered = list.filter(r => {
    if (!q.trim()) return true;
    const hay = `${renderPrimary(r) || ''} ${renderSecondary ? (renderSecondary(r) || '') : ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const allChecked = filtered.length > 0 && filtered.every(r => selected[r.id]);
  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    const n = { ...selected };
    filtered.forEach(r => { n[r.id] = !allChecked; });
    setSelected(n);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle style={{ color: '#010A21' }}>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`${testid}-empty`}>{emptyText}</p>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} className="pl-9 bg-background border-border" data-testid={`${testid}-search`} />
            </div>
            <div className="flex items-center justify-between px-1">
              <button onClick={toggleAll} className="text-xs text-[#1B4A76] hover:underline" data-testid={`${testid}-select-all`}>{allChecked ? 'Deselect all' : 'Select all'}</button>
              <span className="text-xs text-muted-foreground" data-testid={`${testid}-selected-count`}>{selectedIds.length} selected</span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
              {filtered.map(r => (
                <label key={r.id} className="flex items-start gap-3 rounded-md border border-border bg-background px-3 py-2 cursor-pointer hover:border-primary/30 transition-colors" data-testid={`${testid}-item-${r.id}`}>
                  <Checkbox checked={!!selected[r.id]} onCheckedChange={() => toggle(r.id)} className="mt-0.5" data-testid={`${testid}-checkbox-${r.id}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }}>{renderPrimary(r)}</span>
                      {renderBadge && renderBadge(r)}
                    </span>
                    {renderSecondary && <span className="block text-xs text-muted-foreground mt-0.5">{renderSecondary(r)}</span>}
                  </span>
                </label>
              ))}
              {filtered.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No matches.</p>}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button disabled={linking || selectedIds.length === 0} onClick={() => onConfirm(selectedIds)} className="bg-primary text-primary-foreground" data-testid={`${testid}-confirm`}>
                {linking ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Linking...</> : <><Link2 className="h-4 w-4 mr-1" /> Link Selected ({selectedIds.length})</>}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default LinkExistingDialog;
